import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental';
import { Bucket } from '../../types';
import { BucketService } from '../../services/bucket.service';
import { CurrencyPipe } from '@angular/common';
import { UiDialogShellComponent } from '../../ui/dialog-shell/dialog-shell.component';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { UiFieldComponent } from '../../ui/field/field.component';
import { UiInputDirective } from '../../ui/input/input.directive';
import { UiButtonDirective } from '../../ui/button/button.directive';

export interface ArchiveBucketData {
  bucket: Bucket;
  onSuccess: () => void;
}

@Component({
  selector: 'app-archive-bucket-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './archive-bucket-dialog.component.html',
  styleUrl: './archive-bucket-dialog.component.scss',
  imports: [
    UiDialogShellComponent,
    ReactiveFormsModule,
    FormsModule,
    UiFieldComponent,
    UiInputDirective,
    UiButtonDirective,
    CurrencyPipe,
  ],
})
export class ArchiveBucketDialogComponent {
  dialogRef = inject<DialogRef<void, ArchiveBucketDialogComponent>>(DialogRef);
  data = inject<ArchiveBucketData>(DIALOG_DATA);
  private bucketService = inject(BucketService);

  public balanceTransferBucket: string | null = null;
  readonly dialogTitle = `Archive ${this.data.bucket.name}?`;
  readonly bucketsQuery = injectQuery(() => this.bucketService.currentMonthBucketsQueryOptions());
  readonly archiveBalanceQuery = injectQuery(() =>
    this.bucketService.archiveBucketTransferredBalanceQueryOptions(this.data.bucket.id),
  );
  readonly archiveBucketMutation = injectMutation(() => this.bucketService.archiveBucketMutationOptions());
  readonly balance = computed(() => this.archiveBalanceQuery.data()?.balance ?? 0);
  readonly finishedLoading = computed(
    () => this.archiveBalanceQuery.isSuccess() && !this.archiveBalanceQuery.isFetching(),
  );

  public availableBuckets = computed(() =>
    (this.bucketsQuery.data() ?? [])
      .filter(bucket => !bucket.archivedMonth)
      .filter(bucket => bucket.id !== this.data.bucket.id)
      .sort((a, b) => (a.name === b.name ? 0 : a.name < b.name ? -1 : 1)),
  );

  public archiveBucket(balanceTransferBucketId: string | null) {
    this.archiveBucketMutation.mutate(
      { bucketId: this.data.bucket.id, transferBalanceBucketId: balanceTransferBucketId ?? undefined },
      {
        onSuccess: () => {
          this.data.onSuccess();
          this.dialogRef.close();
        },
      },
    );
  }

  public cancel() {
    this.dialogRef.close();
  }
}
